import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MobileLayout } from '../components/MobileLayout';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Tabs } from '../components/ui/Tabs';
import { SharingOpportunitiesWidget } from '../components/SharingOpportunitiesWidget';
import { Plus, X, Check, MapPin, Users, CalendarClock, Sparkles, Zap } from 'lucide-react';
import { useMaintenance, RequestStatus, MaintenanceRequest, Trade } from '../context/MaintenanceContext';
const STATUS_BADGE: Record<RequestStatus, {
  label: string;
  cls: string;
}> = {
  in_attesa_aggregazione: {
    label: 'In attesa di aggregazione',
    cls: 'bg-amber-50 text-amber-700 border-amber-200'
  },
  in_ricerca: {
    label: 'In ricerca fornitore',
    cls: 'bg-amber-50 text-amber-700 border-amber-200'
  },
  assegnata: {
    label: 'Assegnata',
    cls: 'bg-muted text-muted-foreground border-border'
  },
  pianificata: {
    label: 'Pianificata',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  },
  in_corso: {
    label: '● In corso',
    cls: 'bg-coral/5 text-coral border-coral/20'
  },
  completata: {
    label: 'Completata',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  }
};
const PROPERTIES = ['Loft Navigli', 'Bilo Porta Romana', 'Studio Isola'];
const PROPERTY_ZONE: Record<string, string> = {
  'Loft Navigli': 'Navigli',
  'Bilo Porta Romana': 'Porta Romana',
  'Studio Isola': 'Isola'
};
const TRADES: Trade[] = ['Idraulico', 'Elettricista', 'Caldaista', 'Tuttofare', 'Fabbro'];
const inputClass = 'w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none bg-white min-h-[44px]';
const labelClass = 'block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5';
// Two-option segmented control
function Segmented<T extends string>({
  options,
  value,
  onChange
}: {
  options: {
    id: T;
    label: string;
  }[];
  value: T;
  onChange: (id: T) => void;
}) {
  return <div className="grid grid-cols-2 gap-1 p-1 bg-muted rounded-xl" data-id="element-3673">
      {options.map(o => {
      const active = o.id === value;
      return <button key={o.id} type="button" onClick={() => onChange(o.id)} aria-pressed={active} className={`h-9 rounded-lg text-sm font-medium transition-colors ${active ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-3674">
            {o.label}
          </button>;
    })}
    </div>;
}
// ---- Create-request bottom sheet ----
function CreateSheet({
  open,
  onClose,
  onCreate
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (input: Parameters<ReturnType<typeof useMaintenance>['createRequest']>[0]) => void;
}) {
  const [mode, setMode] = useState<'pulizia' | 'urgente'>('pulizia');
  const [property, setProperty] = useState(PROPERTIES[0]);
  // pulizia
  const [periodMode, setPeriodMode] = useState<'singola' | 'periodo'>('singola');
  const [date, setDate] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [tipo, setTipo] = useState<'ordinaria' | 'straordinaria'>('ordinaria');
  // urgente
  const [trade, setTrade] = useState<Trade>(TRADES[0]);
  // shared
  const [detail, setDetail] = useState('');
  const resetForm = () => {
    setMode('pulizia');
    setProperty(PROPERTIES[0]);
    setPeriodMode('singola');
    setDate('');
    setDateFrom('');
    setDateTo('');
    setTipo('ordinaria');
    setTrade(TRADES[0]);
    setDetail('');
  };
  // Close on Escape + lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'pulizia') {
      const isStraordinaria = tipo === 'straordinaria';
      const when = periodMode === 'singola' ? date || 'Da definire' : dateFrom && dateTo ? `${dateFrom} → ${dateTo}` : 'Da definire';
      onCreate({
        title: isStraordinaria ? 'Pulizia straordinaria' : 'Pulizia ordinaria',
        detail,
        property,
        zone: PROPERTY_ZONE[property],
        type: 'pulizia-straordinaria',
        urgency: isStraordinaria ? 'urgente' : 'normale',
        price: isStraordinaria ? 120 : 80,
        date: when
      });
    } else {
      onCreate({
        title: detail.trim() || 'Manutenzione urgente',
        detail,
        property,
        zone: PROPERTY_ZONE[property],
        type: 'manutenzione',
        trade,
        urgency: 'urgente',
        price: 90,
        date: 'Oggi'
      });
    }
    resetForm();
    onClose();
  };
  return <AnimatePresence data-id="element-3675">
      {open && <motion.div className="fixed inset-0 z-50 flex items-end justify-center lg:items-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.15
    }} data-id="element-3676">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" data-id="element-3677" />
          {/* Sheet */}
          <motion.div role="dialog" aria-modal="true" aria-label="Nuovo intervento" initial={{
        y: '100%',
        opacity: 0.6
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: '100%',
        opacity: 0.6
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 320
      }} className="relative w-full lg:max-w-lg bg-white rounded-t-2xl lg:rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto" data-id="element-3678">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-border px-5 py-4 flex items-center justify-between" data-id="element-3679">
              <h2 className="text-base font-bold text-navy" data-id="element-3680">
                Nuovo intervento
              </h2>
              <button type="button" onClick={onClose} aria-label="Chiudi" className="p-2 -mr-2 rounded-full hover:bg-muted transition-colors" data-id="element-3681">
                <X size={20} className="text-muted-foreground" data-id="element-3682" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-5" data-id="element-3683">
              {/* Type chooser */}
              <Segmented value={mode} onChange={setMode} options={[{
            id: 'pulizia',
            label: 'Pulizia'
          }, {
            id: 'urgente',
            label: 'Urgente'
          }]} data-id="element-3684" />

              {/* Property */}
              <div data-id="element-3685">
                <label className={labelClass} data-id="element-3686">Proprietà</label>
                <select value={property} onChange={e => setProperty(e.target.value)} className={inputClass} data-id="element-3687">
                  {PROPERTIES.map(p => <option key={p} value={p} data-id="element-3688">
                      {p}
                    </option>)}
                </select>
              </div>

              {mode === 'pulizia' ? <>
                  {/* Quando */}
                  <div data-id="element-3689">
                    <label className={labelClass} data-id="element-3690">Quando</label>
                    <Segmented value={periodMode} onChange={setPeriodMode} options={[{
                id: 'singola',
                label: 'Singola giornata'
              }, {
                id: 'periodo',
                label: 'Periodo'
              }]} data-id="element-3691" />
                    {periodMode === 'singola' ? <div className="mt-3" data-id="element-3692">
                        <input type="date" value={date} onChange={e => setDate(e.target.value)} className={inputClass} aria-label="Data" data-id="element-3693" />
                      </div> : <div className="mt-3 grid grid-cols-2 gap-3" data-id="element-3694">
                        <div data-id="element-3695">
                          <span className="block text-[11px] text-muted-foreground mb-1" data-id="element-3696">
                            Dal
                          </span>
                          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className={inputClass} aria-label="Dal" data-id="element-3697" />
                        </div>
                        <div data-id="element-3698">
                          <span className="block text-[11px] text-muted-foreground mb-1" data-id="element-3699">
                            Al
                          </span>
                          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className={inputClass} aria-label="Al" data-id="element-3700" />
                        </div>
                      </div>}
                  </div>

                  {/* Tipo */}
                  <div data-id="element-3701">
                    <label className={labelClass} data-id="element-3702">Tipo di pulizia</label>
                    <Segmented value={tipo} onChange={setTipo} options={[{
                id: 'ordinaria',
                label: 'Ordinaria'
              }, {
                id: 'straordinaria',
                label: 'Straordinaria'
              }]} data-id="element-3703" />
                  </div>
                </> : <div data-id="element-3704">
                  <label className={labelClass} data-id="element-3705">Tipo di fornitore</label>
                  <select value={trade} onChange={e => setTrade(e.target.value as Trade)} className={inputClass} data-id="element-3706">
                    {TRADES.map(t => <option key={t} value={t} data-id="element-3707">
                        {t}
                      </option>)}
                  </select>
                </div>}

              {/* Detail */}
              <div data-id="element-3708">
                <label className={labelClass} data-id="element-3709">Cosa serve</label>
                <textarea value={detail} onChange={e => setDetail(e.target.value)} rows={3} placeholder={mode === 'pulizia' ? 'Es. Deep cleaning prima del prossimo check-in' : 'Es. Perdita rubinetto in cucina'} className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none resize-none" data-id="element-3710" />
              </div>

              <Button className="w-full" type="submit" data-id="element-3711">
                Invia richiesta
              </Button>
            </form>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
// ---- Compact shared-aggregation row ----
function SharedRow({
  agg,
  joined,
  onJoin
}: {
  agg: MaintenanceRequest;
  joined: boolean;
  onJoin: () => void;
}) {
  const filled = agg.aggregation?.filled ?? 0;
  const total = agg.aggregation?.total ?? 0;
  const display = joined ? Math.min(filled + 1, total) : filled;
  return <div className="bg-white border border-border rounded-xl p-4 flex items-center justify-between gap-3" data-id="element-3712">
      <div className="min-w-0" data-id="element-3713">
        <h3 className="text-sm font-semibold text-foreground truncate" data-id="element-3714">
          {agg.title}
        </h3>
        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1 flex-wrap" data-id="element-3715">
          <span className="inline-flex items-center gap-1" data-id="element-3716">
            <MapPin size={12} data-id="element-3717" />
            {agg.zone}
          </span>
          <span className="inline-flex items-center gap-1 text-coral font-medium" data-id="element-3718">
            <Users size={12} data-id="element-3719" />
            {display}/{total} host
          </span>
          {agg.aggregation?.estimateDate && <span className="inline-flex items-center gap-1" data-id="element-3720">
              <CalendarClock size={12} data-id="element-3721" />
              {agg.aggregation.estimateDate}
            </span>}
        </div>
      </div>
      {joined ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 flex-shrink-0" data-id="element-3722">
          <Check size={15} data-id="element-3723" />
          Unito
        </span> : <button onClick={onJoin} className="px-3 h-9 bg-coral text-white rounded-lg text-sm font-semibold hover:bg-coral-600 transition-colors whitespace-nowrap flex-shrink-0" data-id="element-3724">
          Unisciti
        </button>}
    </div>;
}
export function RichiediInterventoPage() {
  const {
    requests,
    openAggregations,
    createRequest,
    joinAggregation
  } = useMaintenance();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [joined, setJoined] = useState<string[]>([]);
  const [showAllShared, setShowAllShared] = useState(false);
  const [activeTab, setActiveTab] = useState<'richieste' | 'pulizie' | 'manutenzioni'>('richieste');
  const visibleShared = useMemo(() => showAllShared ? openAggregations : openAggregations.slice(0, 3), [openAggregations, showAllShared]);
  // Auto-open the create sheet when navigated here from the header's
  // "Nuovo intervento" button.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if ((location.state as {
      openNew?: boolean;
    } | null)?.openNew) {
      setSheetOpen(true);
      navigate(location.pathname, {
        replace: true,
        state: {}
      });
    }
  }, [location, navigate]);
  return <MobileLayout data-id="element-3725">
      {/* Header + primary CTA */}
      <div className="mb-6 flex items-center justify-between gap-3" data-id="element-3726">
        <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-3727">
          Richiedi intervento
        </h1>
        <Button className="flex-shrink-0" onClick={() => setSheetOpen(true)} data-id="element-3728">
          <Plus size={18} data-id="element-3729" />
          <span className="hidden sm:inline" data-id="element-3730">Nuovo intervento</span>
          <span className="sm:hidden" data-id="element-3731">Nuovo</span>
        </Button>
      </div>

      {/* Section tabs */}
      <Tabs className="mb-6" scrollable value={activeTab} onValueChange={id => setActiveTab(id as 'richieste' | 'pulizie' | 'manutenzioni')} items={[{
      id: 'richieste',
      label: 'Le mie richieste'
    }, {
      id: 'pulizie',
      label: 'Pulizie condivise'
    }, {
      id: 'manutenzioni',
      label: 'Manutenzioni condivise'
    }]} data-id="element-3732" />

      {/* TAB: Le mie richieste */}
      {activeTab === 'richieste' && (requests.length > 0 ? <div className="space-y-3" data-id="element-3733">
            {requests.map(r => <Card key={r.id} data-id="element-3734">
                <CardContent className="flex items-start justify-between gap-3" data-id="element-3735">
                  <div className="min-w-0" data-id="element-3736">
                    <div className="flex items-center gap-2 mb-1" data-id="element-3737">
                      <h3 className="text-sm font-semibold text-foreground truncate" data-id="element-3738">
                        {r.title}
                      </h3>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border flex-shrink-0 ${STATUS_BADGE[r.status].cls}`} data-id="element-3739">
                        {STATUS_BADGE[r.status].label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap" data-id="element-3740">
                      <span className="flex items-center gap-1" data-id="element-3741">
                        <MapPin size={12} data-id="element-3742" />
                        {r.property} · {r.zone}
                      </span>
                      {r.aggregation && <span className="text-coral font-medium" data-id="element-3743">
                          {r.aggregation.filled}/{r.aggregation.total} host
                        </span>}
                      {r.providerName && <span data-id="element-3744">· {r.providerName}</span>}
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-foreground flex-shrink-0" data-id="element-3745">
                    €{r.price}
                  </span>
                </CardContent>
              </Card>)}
          </div> : <div className="bg-white border border-border rounded-xl p-10 text-center" data-id="element-3746">
            <p className="text-sm font-medium text-foreground" data-id="element-3747">
              Nessuna richiesta ancora
            </p>
            <p className="text-xs text-muted-foreground mt-1" data-id="element-3748">
              Tocca “Nuovo intervento” per crearne una.
            </p>
          </div>)}

      {/* TAB: Pulizie condivise */}
      {activeTab === 'pulizie' && <div data-id="element-3749">
          <SharingOpportunitiesWidget data-id="element-3750" />
        </div>}

      {/* TAB: Manutenzioni condivise */}
      {activeTab === 'manutenzioni' && (openAggregations.length > 0 ? <div data-id="element-3751">
            <div className="flex items-baseline justify-between mb-3" data-id="element-3752">
              <p className="text-xs text-muted-foreground flex items-start gap-1.5" data-id="element-3753">
                <Users size={13} className="text-coral flex-shrink-0 mt-0.5" data-id="element-3754" />
                Unisciti a una richiesta aperta: al raggiungimento del minimo di
                host l’intervento viene pianificato e il costo è condiviso.
              </p>
              {openAggregations.length > 3 && <button onClick={() => setShowAllShared(v => !v)} className="text-xs font-medium text-coral hover:underline flex-shrink-0 ml-3" data-id="element-3755">
                  {showAllShared ? 'Mostra meno' : 'Vedi tutte'}
                </button>}
            </div>
            <div className="space-y-3" data-id="element-3756">
              {visibleShared.map(agg => <SharedRow key={agg.id} agg={agg} joined={joined.includes(agg.id)} onJoin={() => {
          joinAggregation(agg.id);
          setJoined(p => [...p, agg.id]);
        }} data-id="element-3757" />)}
            </div>
          </div> : <div className="bg-white border border-border rounded-xl p-10 text-center" data-id="element-3758">
            <p className="text-sm font-medium text-foreground" data-id="element-3759">
              Nessuna manutenzione condivisa in zona
            </p>
            <p className="text-xs text-muted-foreground mt-1" data-id="element-3760">
              Quando altri host aprono una richiesta condivisa, la vedrai qui.
            </p>
          </div>)}

      {/* Create sheet */}
      <CreateSheet open={sheetOpen} onClose={() => setSheetOpen(false)} onCreate={input => createRequest(input)} data-id="element-3761" />
    </MobileLayout>;
}